import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Trophy, Shuffle, Sparkles, Loader2 } from 'lucide-react';

const GROUP_NAMES = ['A', 'B', 'C'];
const SPIN_INTERVAL_MS = 80;
const SPIN_DURATION_MS = 1800;

const SlotReel = ({ candidates, locked, current, label, color = 'primary' }) => (
  <div className="flex-1 min-w-0">
    <p className="text-xs uppercase tracking-wider text-slate-500 mb-2 text-center">{label}</p>
    <div
      className={`relative h-32 sm:h-40 rounded-2xl border-2 flex items-center justify-center overflow-hidden ${
        locked
          ? color === 'gold'
            ? 'border-gold bg-gold/10'
            : 'border-primary bg-primary/10'
          : 'border-slate-700 bg-slate-900'
      } transition-all duration-300`}
    >
      {!current ? (
        <span className="text-slate-600 text-sm">In attesa...</span>
      ) : (
        <span
          key={current}
          className={`font-numbers text-6xl sm:text-7xl font-black ${
            locked ? (color === 'gold' ? 'text-gold' : 'text-primary') : 'text-white animate-pulse'
          }`}
        >
          {current}
        </span>
      )}
      {locked && (
        <Sparkles className={`absolute top-2 right-2 w-5 h-5 ${color === 'gold' ? 'text-gold' : 'text-primary'} animate-pulse`} />
      )}
    </div>
  </div>
);

/**
 * Dialog di sorteggio per i quarti.
 * - Step 1: anima la 1° classificata (A/B/C)
 * - Step 2: anima la 2° classificata (rimanenti)
 * - Step 3: mostra preview accoppiamenti e bottone Conferma → onConfirm({first, second})
 */
export const KnockoutDrawDialog = ({ open, onClose, onConfirm }) => {
  const [step, setStep] = useState(0); // 0 idle, 1 spinning first, 2 first locked, 3 spinning second, 4 second locked
  const [first, setFirst] = useState(null);
  const [second, setSecond] = useState(null);
  const [reelFirst, setReelFirst] = useState(null);
  const [reelSecond, setReelSecond] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const tickRef = useRef(null);

  useEffect(() => {
    if (!open) {
      // Reset on close
      setStep(0);
      setFirst(null);
      setSecond(null);
      setReelFirst(null);
      setReelSecond(null);
      setSubmitting(false);
      if (tickRef.current) clearInterval(tickRef.current);
    }
  }, [open]);

  const stopSpin = () => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  };

  const drawFirst = () => {
    if (step !== 0 && step !== 4) return;
    stopSpin();
    setStep(1);
    setFirst(null);
    setSecond(null);
    setReelSecond(null);

    const pool = [...GROUP_NAMES];
    const final = pool[Math.floor(Math.random() * pool.length)];
    let i = 0;
    tickRef.current = setInterval(() => {
      setReelFirst(pool[i % pool.length]);
      i++;
    }, SPIN_INTERVAL_MS);

    setTimeout(() => {
      stopSpin();
      setReelFirst(final);
      setFirst(final);
      setStep(2);
    }, SPIN_DURATION_MS);
  };

  const drawSecond = () => {
    if (step !== 2) return;
    stopSpin();
    setStep(3);

    const pool = GROUP_NAMES.filter((g) => g !== first);
    const final = pool[Math.floor(Math.random() * pool.length)];
    let i = 0;
    tickRef.current = setInterval(() => {
      setReelSecond(pool[i % pool.length]);
      i++;
    }, SPIN_INTERVAL_MS);

    setTimeout(() => {
      stopSpin();
      setReelSecond(final);
      setSecond(final);
      setStep(4);
    }, SPIN_DURATION_MS);
  };

  const handleConfirm = async () => {
    if (!first || !second) return;
    setSubmitting(true);
    try {
      await onConfirm({ picked_first_group: first, picked_second_group: second });
    } finally {
      setSubmitting(false);
    }
  };

  // Spacebar shortcut
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.code !== 'Space') return;
      e.preventDefault();
      if (step === 0 || step === 4) {
        if (step === 4) return;
        drawFirst();
      } else if (step === 2) {
        drawSecond();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, step, first]); // eslint-disable-line react-hooks/exhaustive-deps

  const otherFirsts = first ? GROUP_NAMES.filter((g) => g !== first).sort() : [];
  const otherSeconds = second ? GROUP_NAMES.filter((g) => g !== second).sort() : [];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !submitting && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl" data-testid="knockout-draw-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-gold" /> Sorteggio Quarti di Finale
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-6 py-2">
          <p className="text-xs text-slate-400 text-center">
            Step 1: estrai una 1° classificata · Step 2: estrai una 2° classificata da un girone diverso · Step 3: conferma
            <br />
            <span className="text-slate-500">Suggerimento: premi <kbd className="px-1.5 py-0.5 bg-slate-800 rounded text-[10px]">SPAZIO</kbd> per sorteggiare lo step successivo.</span>
          </p>

          <div className="flex gap-3 sm:gap-4">
            <SlotReel
              candidates={GROUP_NAMES}
              locked={step >= 2}
              current={reelFirst}
              label="1° classificata"
              color="primary"
            />
            <div className="flex items-center justify-center text-slate-500 font-heading text-2xl pt-6">VS</div>
            <SlotReel
              candidates={GROUP_NAMES.filter((g) => g !== first)}
              locked={step >= 4}
              current={reelSecond}
              label="2° classificata"
              color="gold"
            />
          </div>

          {step === 0 && (
            <Button onClick={drawFirst} className="w-full bg-primary text-slate-950 hover:bg-primary/90" data-testid="draw-first-btn">
              <Shuffle className="w-4 h-4 mr-2" /> Sorteggia 1° classificata
            </Button>
          )}
          {step === 2 && (
            <Button onClick={drawSecond} className="w-full bg-gold text-slate-950 hover:bg-gold/90" data-testid="draw-second-btn">
              <Shuffle className="w-4 h-4 mr-2" /> Sorteggia 2° classificata
            </Button>
          )}
          {step === 1 || step === 3 ? (
            <Button disabled className="w-full">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sorteggio in corso...
            </Button>
          ) : null}

          {step === 4 && (
            <div className="bg-slate-950 border border-slate-800 rounded-xl p-4 space-y-2" data-testid="qf-preview">
              <p className="text-xs uppercase text-slate-500 tracking-wider mb-2">Accoppiamenti generati</p>
              <div className="flex items-center justify-between text-sm">
                <span className="font-heading"><span className="text-primary">1° {first}</span> vs <span className="text-gold">2° {second}</span></span>
                <span className="text-[10px] text-slate-500">Sorteggio</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="font-heading">1° {otherFirsts[0]} vs miglior 3°</span>
                <span className="text-[10px] text-slate-500">Auto</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="font-heading">1° {otherFirsts[1]} vs seconda miglior 3°</span>
                <span className="text-[10px] text-slate-500">Auto</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="font-heading">2° {otherSeconds[0]} vs 2° {otherSeconds[1]}</span>
                <span className="text-[10px] text-slate-500">Auto</span>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Annulla</Button>
          {step === 4 && (
            <Button
              onClick={handleConfirm}
              disabled={submitting}
              className="bg-gold text-slate-950 hover:bg-gold/90"
              data-testid="confirm-draw-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trophy className="w-4 h-4 mr-2" />}
              Conferma e Genera Quarti
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default KnockoutDrawDialog;
